/**
 * Page-level slots, hosted by the shell around the routed page so no
 * module file changes: a mode can wrap or replace a whole screen
 * (Dentrix Family File blocks, Open Dental Account, Eaglesoft Go To Day
 * panel) while the default page stays available as `children`. Finer
 * slots inside a page (ledger.table, chart.header, schedule.apptBlock) are
 * hosted by the page itself.
 */
import { useParams, useSearch } from '@tanstack/react-router';
import { imagingSkinFor, type ImagingSystem } from '@velora/contracts/skins';
import type { ReactNode } from 'react';
import { runAction } from '../actions/registry.js';
import { useSkin, SkinSlot } from './SkinProvider.js';
import type { SlotName } from './types.js';
import { useImagingViewerApi } from './viewerApi.js';

type PageSlot = Extract<
  SlotName,
  | 'home.page'
  | 'schedule.page'
  | 'patients.list'
  | 'patients.file'
  | 'ledger.page'
  | 'billing.page'
  | 'chart.page'
  | 'notes.page'
  | 'perio.page'
  | 'txplan.page'
  | 'imaging.viewer'
  | 'recall.page'
  | 'reminders.page'
  | 'claims.page'
  | 'insurance.page'
  | 'reports.page'
  | 'frontdesk.documents'
  | 'frontdesk.tasks'
  | 'frontdesk.close'
  | 'communications.page'
>;

/** Route path to page slot. */
export const PAGE_SLOTS: Readonly<Record<string, PageSlot>> = {
  '/home': 'home.page',
  '/schedule': 'schedule.page',
  '/patients': 'patients.list',
  '/patients/$patientId': 'patients.file',
  '/patients/$patientId/account': 'ledger.page',
  '/billing': 'billing.page',
  '/patients/$patientId/notes': 'notes.page',
  '/charting': 'chart.page',
  '/patients/$patientId/chart': 'chart.page',
  '/perio': 'perio.page',
  '/patients/$patientId/perio': 'perio.page',
  '/treatment-plans': 'txplan.page',
  '/patients/$patientId/treatment': 'txplan.page',
  '/imaging': 'imaging.viewer',
  // Whole-screen windows for the familiar modes (DTX-10, OD-27, FES-SH-1).
  '/recall': 'recall.page',
  '/reminders': 'reminders.page',
  '/claims': 'claims.page',
  '/claims/era': 'claims.page',
  '/claims/batch': 'claims.page',
  '/claims/insurance-payment': 'claims.page',
  '/insurance': 'insurance.page',
  '/analytics': 'reports.page',
  '/analytics/reports': 'reports.page',
  '/frontdesk/patient': 'frontdesk.documents',
  '/frontdesk/tasks': 'frontdesk.tasks',
  '/frontdesk/close': 'frontdesk.close',
  '/communications': 'communications.page',
};

/**
 * Opens the mode's Select Patient window (the `patient.select` action the
 * Select Patient host registers). Passed to every page slot so a patient
 * module with no patient shows its own empty layout and asks for one (OD-02).
 */
export function requestSelectPatient(): void {
  runAction('patient.select');
}

/** A `?patient=` value usable as an id (a non-empty string or a number), else null. */
function patientFromSearch(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function PageSlotHost({
  path,
  imagingSystem,
  children,
}: {
  path: string | undefined;
  /** The practice's imaging setting (`null` = match the mode). */
  imagingSystem: ImagingSystem | null;
  children: ReactNode;
}) {
  const skin = useSkin();
  const params = useParams({ strict: false }) as { patientId?: string };
  const search = useSearch({ strict: false }) as { patient?: unknown; velora?: unknown };
  const viewerApi = useImagingViewerApi();
  const name = path ? PAGE_SLOTS[path] : undefined;
  if (!name || !skin.slots[name]) return <>{children}</>;
  // NEW-WF-07: `?velora=1` opens the Velora patient record in any mode, so a
  // mode's window can hand off to a workflow it does not draw itself
  // (coverage drawer, merge, status change).
  if (name === 'patients.file' && (search.velora === '1' || search.velora === 1)) {
    return <>{children}</>;
  }
  // The route's patient, else the `?patient=` a menu passes to a list window
  // (Continuing Care, Messages, Documents: /recall?patient=<id>).
  const patientId = params.patientId ?? patientFromSearch(search.patient);
  if (name === 'imaging.viewer') {
    return (
      <SkinSlot
        name={name}
        model={{
          patientId,
          requestSelectPatient,
          imagingSkin: imagingSkinFor(skin.skin, imagingSystem),
          viewerApi,
        }}
      >
        {children}
      </SkinSlot>
    );
  }
  return (
    <SkinSlot name={name} model={{ patientId, requestSelectPatient }}>
      {children}
    </SkinSlot>
  );
}

/**
 * The mode's always-on component (`shell.global`): global verbs, key
 * layers, counters. Mounted by the shell for as long as the mode is active.
 */
export function ShellGlobalHost() {
  const skin = useSkin();
  if (!skin.slots['shell.global']) return null;
  return (
    <SkinSlot name="shell.global" model={{ patientId: null, requestSelectPatient }}>
      {null}
    </SkinSlot>
  );
}
